import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { galleriesApi } from '../../../api/adminApi';
import { parseGalleryImageEntries, galleryId } from './galleryHelpers';
import './GalleryView.css';

function GalleryView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [appendFiles, setAppendFiles] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const [removingId, setRemovingId] = useState(null);
  const [deleteGalleryOpen, setDeleteGalleryOpen] = useState(false);
  const [deletingGallery, setDeletingGallery] = useState(false);

  const loadGallery = (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!id) return Promise.resolve();
    if (!silent) setLoading(true);
    setError('');
    return galleriesApi
      .getOne(id)
      .then((data) => {
        setGallery(data);
      })
      .catch((err) => setError(err.userMessage || 'Failed to load gallery'))
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    loadGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openEditModal = () => {
    if (!gallery) return;
    setTitle(gallery.title ?? '');
    setAppendFiles([]);
    setSaveError('');
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setAppendFiles([]);
    setSaveError('');
    const input = document.getElementById('gv-modal-append-images');
    if (input) input.value = '';
  };

  const handleAppendFiles = (e) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    setAppendFiles(list);
  };

  const handleSave = (e) => {
    e.preventDefault();
    setSaveError('');
    const gid = galleryId(gallery);
    if (!gid) return;

    const trimmed = title.trim();
    if (!trimmed) {
      setSaveError('Title is required.');
      return;
    }

    const hasFiles = appendFiles.length > 0;
    const titleChanged = trimmed !== (gallery?.title ?? '').trim();
    if (!titleChanged && !hasFiles) {
      setSaveError('Change the title or choose images to append.');
      return;
    }

    setSaving(true);
    const payload = {};
    if (titleChanged) payload.title = trimmed;
    if (hasFiles) payload.images = appendFiles;

    galleriesApi
      .update(gid, payload)
      .then(() => loadGallery({ silent: true }))
      .then(() => closeEditModal())
      .catch((err) => setSaveError(err.userMessage || 'Failed to save.'))
      .finally(() => setSaving(false));
  };

  const handleRemoveImage = (imageId) => {
    const gid = galleryId(gallery);
    if (!gid || !imageId) return;
    setError('');
    setRemovingId(imageId);
    galleriesApi
      .deleteImage(gid, imageId)
      .then(() => loadGallery({ silent: true }))
      .catch((err) => setError(err.userMessage || 'Failed to remove image'))
      .finally(() => setRemovingId(null));
  };

  const handleDeleteGallery = () => {
    const gid = galleryId(gallery);
    if (!gid) return;
    setDeletingGallery(true);
    galleriesApi
      .delete(gid)
      .then(() => navigate('/admin/galleries', { replace: true }))
      .catch((err) => {
        setError(err.userMessage || 'Failed to delete gallery');
        setDeleteGalleryOpen(false);
      })
      .finally(() => setDeletingGallery(false));
  };

  if (loading) {
    return <div className="gv-loading">Loading gallery…</div>;
  }

  if (error && !gallery) {
    return (
      <div className="gv-wrap">
        <div className="gv-header">
          <Link to="/admin/galleries" className="gv-back">
            ← Galleries
          </Link>
        </div>
        <div className="gv-error" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const gid = galleryId(gallery);
  const imageEntries = parseGalleryImageEntries(gallery);

  return (
    <div className="gv-wrap">
      <div className="gv-header">
        <Link to="/admin/galleries" className="gv-back">
          ← Galleries
        </Link>
        <h2 className="gv-title">{gallery?.title || 'Gallery'}</h2>
        <div className="gv-header-actions">
          <button
            type="button"
            className="gv-btn gv-btn-edit-gallery"
            onClick={openEditModal}
            disabled={!gid}
          >
            Edit gallery
          </button>
          <button type="button" className="gv-btn gv-btn-danger-outline" onClick={() => setDeleteGalleryOpen(true)}>
            Delete gallery
          </button>
        </div>
      </div>

      {gallery?.creator?.email && (
        <p className="gv-meta">
          Created by {gallery.creator.name || gallery.creator.email}
          {gallery.creator.name ? ` · ${gallery.creator.email}` : ''}
        </p>
      )}

      {error && (
        <div className="gv-error gv-error--inline" role="alert">
          {error}
        </div>
      )}

      <div className="gv-card gv-card--images">
        <h3 className="gv-section-title">Images ({imageEntries.length})</h3>
        <p className="gv-hint gv-hint--tight">
          Remove images here. To change the title or add more photos, use <strong>Edit gallery</strong>.
        </p>
        {imageEntries.length === 0 ? (
          <p className="gv-empty-images">No images in this gallery yet.</p>
        ) : (
          <ul className="gv-image-grid">
            {imageEntries.map((img) => (
              <li key={img.key} className="gv-image-cell">
                <img src={img.url} alt="" className="gv-image-thumb" />
                {img.id ? (
                  <button
                    type="button"
                    className="gv-btn gv-btn-remove-img"
                    disabled={removingId === img.id}
                    onClick={() => handleRemoveImage(img.id)}
                  >
                    {removingId === img.id ? 'Removing…' : 'Remove'}
                  </button>
                ) : (
                  <span className="gv-no-id-hint">ID missing — cannot remove via API</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Modal
        isOpen={editModalOpen}
        onRequestClose={() => {
          if (!saving) closeEditModal();
        }}
        contentLabel="Edit gallery"
        className="gv-edit-modal-content"
        overlayClassName="gv-edit-modal-overlay"
      >
        <div className="gv-edit-modal-inner">
          <h3 id="gv-edit-modal-title">Edit gallery</h3>
          <p className="gv-edit-modal-lead">
            Update the title and/or append new images. Existing images stay on the gallery until you remove them from the
            main view.
          </p>
          <form className="gv-form" onSubmit={handleSave}>
            {saveError && (
              <div className="gv-form-error" role="alert">
                {saveError}
              </div>
            )}

            {imageEntries.length > 0 && (
              <div className="gv-edit-modal-current">
                <p className="gv-edit-modal-current-label">Current photos ({imageEntries.length})</p>
                <ul className="gv-edit-modal-thumb-grid" aria-label="Current gallery photos">
                  {imageEntries.map((img) => (
                    <li key={img.key}>
                      <img src={img.url} alt="" className="gv-edit-modal-thumb" />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="gv-field">
              <label htmlFor="gv-modal-title">Title *</label>
              <input
                id="gv-modal-title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="gv-field">
              <label htmlFor="gv-modal-append-images">Append images</label>
              <input
                id="gv-modal-append-images"
                type="file"
                accept="image/*"
                multiple
                onChange={handleAppendFiles}
                disabled={saving}
              />
              {appendFiles.length > 0 && (
                <p className="gv-files-hint">{appendFiles.length} new file(s) will be uploaded on save.</p>
              )}
            </div>

            <div className="gv-edit-modal-actions">
              <button type="button" className="gv-edit-modal-cancel" onClick={closeEditModal} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="gv-btn gv-btn-edit-submit" disabled={saving || !gid}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {deleteGalleryOpen && (
        <div className="gv-delete-modal" role="dialog" aria-modal="true" aria-labelledby="gv-del-title">
          <div className="gv-delete-backdrop" onClick={() => !deletingGallery && setDeleteGalleryOpen(false)} aria-hidden />
          <div className="gv-delete-box">
            <h3 id="gv-del-title">Delete this gallery?</h3>
            <p>
              This soft-deletes the gallery and removes its images from storage. This cannot be undone from the admin
              UI.
            </p>
            <div className="gv-delete-actions">
              <button
                type="button"
                className="gv-btn gv-btn-muted"
                onClick={() => setDeleteGalleryOpen(false)}
                disabled={deletingGallery}
              >
                Cancel
              </button>
              <button type="button" className="gv-btn gv-btn-danger" onClick={handleDeleteGallery} disabled={deletingGallery}>
                {deletingGallery ? 'Deleting…' : 'Delete gallery'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GalleryView;
